import capitalize from 'lodash.capitalize';
import { useSelector } from 'react-redux';
import {
  selectStreamingCustomers,
  selectDownloadCustomers,
} from 'redux/reducers/customers';
import { RootState } from 'redux/rootReducer';
import { useMemo, useCallback } from 'react';
import { allPlatforms } from 'components/utility/FilterDisplayName/FilterDisplayName';
import { ITopicFilterState, TopicFilterNames } from '../shared';

export default function useFilterNames(
  topicFilterState: ITopicFilterState,
  validFilterNames: TopicFilterNames[],
  isLargeScreen: boolean
): string[] {
  const countries = useSelector((state: RootState) => state.countries.data);
  const exCountries = useSelector((state: RootState) => state.exCountries.data);
  const streamingCustomers = useSelector(selectStreamingCustomers);
  const downloadCustomers = useSelector(selectDownloadCustomers);
  const regions = useSelector((state: RootState) => state.regions.data);
  const customRegions = Object.values(
    useSelector((state: RootState) => state.customRegions.data)
  );
  const genres = useSelector((state: RootState) => state.genres.data);

  const findCountry = (countryId: any): ((country: any) => boolean) => {
    if (typeof countryId !== 'string') {
      return (country: any): boolean => countryId.includes(country.id);
    }

    return (country: any): boolean => country.id === countryId;
  };
  const findCustomer = (customerId: any): ((customer: any) => boolean) => {
    return (customer: any): boolean => customer.id === customerId;
  };
  const findRegion = (regionId: any): ((region: any) => boolean) => {
    if (regionId?.length) {
      return (region: any): boolean => regionId.includes(region.id);
    }
    return (region: any): boolean => region.id === regionId;
  };
  const findGenre = (genreId: any): ((genre: any) => boolean) => {
    if (genreId?.length) {
      return (genre: any): boolean =>
        genreId.includes(genre.genreCode.toString());
    }
    return (genre: any): boolean => genre.genreCode.toString() === genreId;
  };

  const resolveFilterValue = useCallback(
    (name: TopicFilterNames, value: any): string => {
      switch (name) {
        case 'customRegion': {
          const region = customRegions.filter(findRegion(value));
          const regionsNames = region.map((g) => g.displayName);
          return regionsNames.join(', ') ?? '';
        }
        case 'region': {
          const region = regions.filter(findRegion(value));
          const regionsNames = region.map((g) => g.displayName);
          return regionsNames.join(', ') ?? '';
        }
        case 'country': {
          const country = countries.filter(findCountry(value));
          if (country.length) {
            return country
              .map((c) => (isLargeScreen ? c.displayName : c.id))
              .join(', ');
          }
          const exCountry = exCountries.filter(findCountry(value));
          if (exCountry.length) {
            return exCountry
              .map((c) => (isLargeScreen ? 'EX ' + c.displayName : c.id))
              .join(', ');
          }
          return '';
        }

        case 'streaming': {
          const streamingCustomer = streamingCustomers.find(
            findCustomer(value)
          );
          return streamingCustomer?.displayName ?? '';
        }
        case 'download': {
          const streamingCustomer = downloadCustomers.find(findCustomer(value));
          return streamingCustomer?.displayName ?? '';
        }
        case 'platform': {
          const platform = allPlatforms.find(findCustomer(value));
          return platform?.label ?? '';
        }
        case 'media': {
          return (value && capitalize(value)) ?? '';
        }
        case 'release': {
          return (value && capitalize(value)) ?? '';
        }
        case 'genre': {
          const genre = genres.filter(findGenre(value));
          const genreNames = genre.map((g) => g.genreName);
          return genreNames.join(', ') ?? '';
        }
        default:
          return '';
      }
    },
    [
      countries,
      exCountries,
      regions,
      genres,
      streamingCustomers,
      downloadCustomers,
      isLargeScreen,
      customRegions,
    ]
  );

  return useMemo(() => {
    return validFilterNames.reduce(
      (acc: any, name: keyof ITopicFilterState): string[] => {
        if (topicFilterState[name]) {
          const filter = resolveFilterValue(name, topicFilterState[name]);
          if (filter) {
            acc.push(filter);
          }
        }
        return acc;
      },
      []
    );
  }, [resolveFilterValue, topicFilterState, validFilterNames]);
}
